import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { GetIncomingBookings_operativeViewer_incomingBookingsForVenue } from '~Providers/BookingsProvider/api/__queries__';
import { useBookings } from '~Providers/BookingsProvider';
import { useLogger } from '~Hooks';

const IncomingBookingBlock = styled.div<{
  gridColumnStart: number;
  gridColumnSpan: number;
  offsetMinutes: number;
  highLight?: boolean;
}>`
  position: absolute;
  left: ${(props) =>
    64 +
    (props.gridColumnStart - 2) * 61 +
    (props.offsetMinutes / 30) *
      61}px; /* 64px label + column position * (60px + 1px gap) + minute offset */
  width: ${(props) =>
    props.gridColumnSpan * 60 + (props.gridColumnSpan - 1)}px; /* span * column width + gaps */
  top: 4px;
  bottom: 4px;
  background: linear-gradient(135deg, #363a4d 0%, #9a87ad 100%);
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  border-radius: 6px;
  padding: 2px 8px;
  color: var(--colorWhite);
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 3;
  box-sizing: border-box;
  opacity: 0.5;
  outline: ${({ highLight }) =>
    highLight
      ? '1px solid var(--colorUIYellow)'
      : 'none'}; /* Highlight with a solid border when selected */
`;

const BookingName = styled.div`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface IncomingBookingBlockItemProps {
  booking: GetIncomingBookings_operativeViewer_incomingBookingsForVenue;
  gridColumnStart: number;
  gridColumnSpan: number;
  offsetMinutes: number;
}

const IncomingBookingBlockItem: React.FC<IncomingBookingBlockItemProps> = ({
  booking,
  gridColumnStart,
  gridColumnSpan,
  offsetMinutes,
}) => {
  const bookings = useBookings();
  const blockRef = useRef<HTMLDivElement>(null);

  // Scroll into view when this incoming booking is highlighted from the sidebar
  useEffect(() => {
    if (bookings.highlightedIncomingBookingId === booking.id && blockRef.current) {
      blockRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [bookings.highlightedIncomingBookingId, booking.id]);

  return (
    <IncomingBookingBlock
      ref={blockRef}
      gridColumnStart={gridColumnStart}
      gridColumnSpan={gridColumnSpan}
      offsetMinutes={offsetMinutes}
      data-incoming-booking-id={booking.id}
      highLight={bookings.highlightedIncomingBookingId === booking.id}
      onClick={() => bookings.actions.setHighlightedIncomingBooking(booking.id)}>
      <BookingName>
        {booking.guestName} - {booking.partySize} guests
      </BookingName>
    </IncomingBookingBlock>
  );
};

export default IncomingBookingBlockItem;
